import { useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useIntl } from 'react-intl';
import { useHistory } from 'react-router-dom';
import { Queue } from '@rossum/api-client/queues';
import {
  IconCopy,
  IconDotsVertical,
  IconFolderSymlink,
  IconTrash,
} from '@rossum/ui/icons/tabler';
import {
  Dialog,
  IconButton,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Menu,
  SvgIcon,
} from '@rossum/ui/material';
import { queueManagerPath } from '../../queue-manager/routes';
import { QueueDelete } from '../../queue-sidebar/sidebar-queues/components/QueueDelete';
import { QueueDuplicate } from '../../queue-sidebar/sidebar-queues/components/QueueDuplicate';
import { QueueMove } from '../../queue-sidebar/sidebar-queues/components/QueueMove';
import { QUERY_KEY_QUEUE } from '../../queues/hooks/useQueue';
import { QUERY_KEY_WORKSPACE } from '../../queues/hooks/useQueueWithWorkspace';
import { useWorkspacesWithQueues } from '../../queues/hooks/useWorkspacesWithQueues';

type QueueActionsMenuProps = {
  queue: Queue;
};

type ActionState =
  | { type: 'idle' }
  | { type: 'move' }
  | { type: 'duplicate' }
  | { type: 'delete' };

export const QueueActionsMenu = ({ queue }: QueueActionsMenuProps) => {
  const intl = useIntl();
  const history = useHistory();
  const queryClient = useQueryClient();
  const [anchorEl, setAnchorEl] = useState<Element | null>(null);
  const [actionState, setActionState] = useState<ActionState>({ type: 'idle' });

  const { allWorkspacesWithQueues } = useWorkspacesWithQueues({
    enableQueries: actionState.type === 'move',
  });

  const handleClose = () => setAnchorEl(null);
  const setIdleState = () => setActionState({ type: 'idle' });

  const handleMoveClick = () => {
    setActionState({ type: 'move' });
    handleClose();
  };

  const handleDuplicateClick = () => {
    setActionState({ type: 'duplicate' });
    handleClose();
  };

  const handleDeleteClick = () => {
    setActionState({ type: 'delete' });
    handleClose();
  };

  const handleMoveSuccess = () => {
    queryClient.invalidateQueries({ queryKey: [QUERY_KEY_QUEUE, queue.id] });
    queryClient.invalidateQueries({ queryKey: [QUERY_KEY_WORKSPACE] });
    setIdleState();
  };

  const handleDuplicateSuccess = (duplicatedQueueId: number) => {
    setIdleState();
    history.push(`/queues/${duplicatedQueueId}/settings`);
  };

  const handleDeleteSuccess = () => {
    setIdleState();
    history.push(queueManagerPath());
  };

  return (
    <>
      <IconButton
        data-cy="queue-actions-menu-button"
        onClick={e => setAnchorEl(e.currentTarget)}
      >
        <SvgIcon fontSize="large">
          <IconDotsVertical />
        </SvgIcon>
      </IconButton>
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleClose}
        slotProps={{
          list: {
            dense: true,
          },
        }}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
      >
        <ListItem disablePadding data-cy="queue-actions-menu-item-move">
          <ListItemButton onClick={handleMoveClick}>
            <ListItemIcon sx={{ minWidth: 32 }}>
              <SvgIcon fontSize="medium">
                <IconFolderSymlink />
              </SvgIcon>
            </ListItemIcon>
            <ListItemText>
              {intl.formatMessage({
                id: 'components.workspaces.menu.labels.move',
              })}
            </ListItemText>
          </ListItemButton>
        </ListItem>
        <ListItem disablePadding data-cy="queue-actions-menu-item-duplicate">
          <ListItemButton onClick={handleDuplicateClick}>
            <ListItemIcon sx={{ minWidth: 32 }}>
              <SvgIcon fontSize="medium">
                <IconCopy />
              </SvgIcon>
            </ListItemIcon>
            <ListItemText>
              {intl.formatMessage({
                id: 'components.workspaces.menu.labels.duplicateQueue',
              })}
            </ListItemText>
          </ListItemButton>
        </ListItem>
        <ListItem disablePadding data-cy="queue-actions-menu-item-delete">
          <ListItemButton onClick={handleDeleteClick}>
            <ListItemIcon sx={{ minWidth: 32 }}>
              <SvgIcon fontSize="medium">
                <IconTrash />
              </SvgIcon>
            </ListItemIcon>
            <ListItemText>
              {intl.formatMessage({
                id: 'components.workspaces.menu.labels.delete',
              })}
            </ListItemText>
          </ListItemButton>
        </ListItem>
      </Menu>

      <Dialog
        open={actionState.type === 'move'}
        onClose={setIdleState}
        PaperProps={{
          sx: { width: 480, minHeight: 213, position: 'fixed' },
          elevation: 2,
        }}
      >
        {actionState.type === 'move' && allWorkspacesWithQueues ? (
          <QueueMove
            queue={queue}
            workspaces={allWorkspacesWithQueues}
            onMove={handleMoveSuccess}
            onCancel={setIdleState}
          />
        ) : null}
      </Dialog>

      <Dialog
        open={actionState.type === 'duplicate'}
        onClose={setIdleState}
        PaperProps={{
          sx: { width: 480, minHeight: 300, position: 'fixed' },
          elevation: 2,
        }}
      >
        {actionState.type === 'duplicate' ? (
          <QueueDuplicate
            queue={queue}
            onDuplicate={handleDuplicateSuccess}
            onCancel={setIdleState}
          />
        ) : null}
      </Dialog>

      <Dialog
        open={actionState.type === 'delete'}
        onClose={setIdleState}
        PaperProps={{
          sx: { width: 480, minHeight: 213, position: 'fixed' },
          elevation: 2,
        }}
      >
        {actionState.type === 'delete' ? (
          <QueueDelete
            queueId={queue.id}
            queueName={queue.name}
            onDelete={handleDeleteSuccess}
            onCancel={setIdleState}
          />
        ) : null}
      </Dialog>
    </>
  );
};
